import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { debounce } from "lodash";
import { Alert } from "@material-ui/lab";
import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import AutoForm from "../form/functional/AutoForm";
import "../grid/layout/DataGridMomSysOrderPurchase.css";
import layoutConf from "../../config/layoutConf";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function EguInputInspectionDetailRegisterDialog(props) {
    const {
        id,
        schema,

        inputInspectionSelect,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const [inputInspectionDetailTargetGrid, setInputInspectionDetailTargetGrid] = useState();
    const [inputInspectionDetailTargetApi, setInputInspectionDetailTargetApi] = useState();
    const [inputInspectionDetailTargetSelect, setInputInspectionDetailTargetSelect] = useState();

    if (schema && !schema.columnMap) {
        let columnMap = {};

        for (const column of schema.columns) {
            columnMap[column.name] = column;
        }

        schema.columnMap = columnMap;
    }

    useEffect(() => {
        (async () => {
            if (inputInspectionDetailTargetGrid) {
                inputInspectionDetailTargetGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setInputInspectionDetailTargetSelect(grid.selectedItems[0]);
                        } else {
                            setInputInspectionDetailTargetSelect();
                        }
                    }, 100)
                );

                inputInspectionDetailTargetGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setInputInspectionDetailTargetSelect(grid.selectedItems[0]);
                        } else {
                            setInputInspectionDetailTargetSelect();
                        }
                    }, 100)
                );
            }
        })();
    }, [inputInspectionDetailTargetGrid]);

    useEffect(() => {
        if (inputInspectionDetailTargetGrid && inputInspectionDetailTargetSelect) {
            setFormData((formData) => {
                let newFormData = {
                    ...formData,

                    materialCode: inputInspectionDetailTargetSelect.materialCode,
                    materialCodeVer: inputInspectionDetailTargetSelect.materialCodeVer,
                    materialName: inputInspectionDetailTargetSelect.materialName,
                    materialTypeName: inputInspectionDetailTargetSelect.materialTypeName,
                    itemSpecName: inputInspectionDetailTargetSelect.itemSpecName,
                    itemUnitName: inputInspectionDetailTargetSelect.itemUnitName,
                    qtyFakeInput: inputInspectionDetailTargetSelect.qtyFakeInput,
                    manufacturedDate: inputInspectionDetailTargetSelect.manufacturedDate,
                    expirationDate: inputInspectionDetailTargetSelect.expirationDate,
                    jaHo: inputInspectionDetailTargetSelect.jaHo,

                    inputInspectionId: inputInspectionSelect.inputInspectionId,
                    materialFakeInputDetailId: inputInspectionDetailTargetSelect.materialFakeInputDetailId,
                    inspectQty:
                        Number(inputInspectionDetailTargetSelect.qtyFakeInput) -
                        Number(inputInspectionDetailTargetSelect.inspectQty),
                };

                for (const column of schema.columns) {
                    if (column?.defaultValue !== undefined) {
                        newFormData[column.name] = column.defaultValue;
                    }
                }

                setOkButtonDisabled(false);

                return newFormData;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema, inputInspectionDetailTargetSelect]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        if (inputInspectionDetailTargetApi) {
            inputInspectionDetailTargetApi.refresh();
        }

        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();

                if (onSuccess) {
                    setTimeout(() => onSuccess(result.data), 0);
                }
            } else {
                if (onSuccess) {
                    setTimeout(() => onSuccess(result.data), 0);
                } else {
                    onNotifySuccess(result.data);
                }
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        notifyFailure(
            t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
        );
    };

    const checkItemValidation = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("edit", column, column.validation, formData[column.name]);

            if (!valid) {
                notifyWarning(msg);
                return false;
            }
        }

        if (
            Number(formData.inspectQty) >
            Number(inputInspectionDetailTargetSelect.qtyFakeInput) -
            Number(inputInspectionDetailTargetSelect.inspectQty)
        ) {
            const msg = t("error.tooBigInspectQty", {
                inspectQty: formData.inspectQty,
                remainQty:
                    Number(inputInspectionDetailTargetSelect.qtyFakeInput) -
                    Number(inputInspectionDetailTargetSelect.inspectQty),
            });

            notifyWarning(msg);
            return false;
        }

        return true;
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidation(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        let url;

        url = `/api/${schema.name}`;

        let apiData = { ...formData };

        let form = new FormData();

        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;

                    form.append(formItem, file.file);
                }
            } else {
                if (apiData[formItem] !== undefined) {
                    json[formItem] = apiData[formItem];
                }
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setFormData(null);

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <React.Fragment>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </React.Fragment>
        );
    };

    // 양식이 다음 틱에 알린 값을 바로 받는다 — 한 틱 더 미루면 그 사이 고른 검사 대상 품목을 양식의 옛 빈 값이 덮는다
    const onChangeFormData = (formData) => {
        setFormData(formData);

        setOkButtonDisabled(false);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setFormData();

        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeaderWithClose>
                <DialogTitle>{t("dialog.title.EguInputInspectionDetail.register")}</DialogTitle>
            </DialogHeaderWithClose>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.5}>
                        <BasicCrudScreenType
                            id="EguVwInputInspectionDetailTarget"
                            embededInOtherScreenType
                            view="EguVwInputInspectionDetailTarget"
                            headerTitle={t("term.eguInputInspectionDetailTarget")}
                            noCreate
                            noEdit
                            noDelete
                            noExcel
                            onInitialized={(grid) => {
                                grid.selectionMode = "Row";
                                setInputInspectionDetailTargetGrid(grid);
                            }}
                            onApiInitialized={(api) => {
                                setInputInspectionDetailTargetApi(api);
                            }}
                            onModifyViewUrl={(url) => `${url}/${inputInspectionSelect?.materialFakeInputId}`}
                        />
                    </ReflexElement>

                    <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />

                    <ReflexElement flex={0.5}>
                        <div style={{ padding: "16px" }}>
                            <AutoForm
                                id="egu-input-inspection-detail-register-form"
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                //labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default EguInputInspectionDetailRegisterDialog;
